import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { alunoService, estagioService, relatorioEstagioService } from '@/services/api';
import { Aluno, Estagio, RelatorioEstagio } from '@/types';
import { DocumentTextIcon, UserIcon, BuildingOfficeIcon, CalendarIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

// Tipo para erros da API
interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
  message?: string;
}

// Tipo para dados de criação de relatório
interface CreateRelatorioData {
  estagioId: number;
  tipo: 'Inicial' | 'Parcial' | 'Final';
  prazoEntrega: string;
  dataEntregue?: string;
  observacao?: string;
}

// Schema de validação para relatório
const relatorioSchema = z.object({
  prazoEntrega: z.string().min(1, 'Prazo de entrega é obrigatório'),
  dataEntregue: z.string().optional(),
  observacao: z.string().optional(),
});

type RelatorioFormData = {
  prazoEntrega: string;
  dataEntregue?: string;
  observacao?: string;
};

const tiposRelatorio = [
  { value: 'Inicial', label: 'Relatório Inicial', color: 'blue' },
  { value: 'Parcial', label: 'Relatório Parcial', color: 'yellow' },
  { value: 'Final', label: 'Relatório Final', color: 'green' },
];

export const GerenciarRelatoriosPage: React.FC = () => {
  const queryClient = useQueryClient();

  // Estados
  const [alunoSelecionado, setAlunoSelecionado] = useState<Aluno | null>(null);
  const [estagioSelecionado, setEstagioSelecionado] = useState<Estagio | null>(null);
  const [relatorioEditando, setRelatorioEditando] = useState<{ estagioId: number; tipo: 'Inicial' | 'Parcial' | 'Final' } | null>(null);

  // Buscar relatórios do aluno selecionado
  const { data: relatoriosAluno, refetch: refetchRelatoriosAluno, isLoading: relatoriosAlunoLoading } = useQuery<RelatorioEstagio[]>({
    queryKey: ['relatorios-aluno', alunoSelecionado?.id],
    queryFn: async () => {
      if (!alunoSelecionado?.id) {
        return [];
      }
      try {
        const result = await relatorioEstagioService.getByAluno(alunoSelecionado.id);
        return Array.isArray(result) ? result : [];
      } catch (error) {
        console.error('Erro ao buscar relatórios do aluno:', error);
        return [];
      }
    },
    enabled: !!alunoSelecionado?.id,
    staleTime: 30000, // 30 segundos
  });

  // Filtrar relatórios do estágio selecionado
  const relatoriosDoEstagioSelecionado = useMemo(() => {
    if (!estagioSelecionado?.id || !relatoriosAluno) {
      return [];
    }
    return relatoriosAluno.filter(relatorio => relatorio.estagioId === estagioSelecionado.id);
  }, [relatoriosAluno, estagioSelecionado?.id]);

  // Quando o estágio selecionado mudar, invalidar cache dos relatórios deste estágio
  useEffect(() => {
    if (estagioSelecionado?.id) {
      // Invalidar apenas a query específica deste aluno
      queryClient.invalidateQueries({ queryKey: ['relatorios-aluno', alunoSelecionado?.id] });
      refetchRelatoriosAluno();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estagioSelecionado?.id, alunoSelecionado?.id]);


  // Mutation para criar relatório
  const createRelatorioMutation = useMutation({
    mutationFn: (data: { estagioId: number; tipo: 'Inicial' | 'Parcial' | 'Final'; prazoEntrega: string; dataEntregue?: string; observacao?: string }) =>
      relatorioEstagioService.create(data),
    onSuccess: async () => {
      toast.success('Relatório criado com sucesso!');
      setRelatorioEditando(null);

      // Pequeno delay para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 100));

      // Invalidar queries relacionadas
      await queryClient.invalidateQueries({ queryKey: ['relatorios-gerenciar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });

      // Forçar refetch imediato dos relatórios do aluno
      if (alunoSelecionado) {
        await refetchRelatoriosAluno();
      }
    },
    onError: (error: ApiError) => {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao criar relatório';
      toast.error(errorMessage);
    },
  });

  // Mutation para atualizar relatório
  const updateRelatorioMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<RelatorioFormData> }) =>
      relatorioEstagioService.update(id, data),
    onSuccess: async () => {
      toast.success('Relatório atualizado com sucesso!');
      // Invalidar queries relacionadas
      await queryClient.invalidateQueries({ queryKey: ['relatorios-gerenciar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      // Forçar refetch dos relatórios do aluno
      await refetchRelatoriosAluno();
      setRelatorioEditando(null);
    },
    onError: (error: ApiError) => {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar relatório';
      toast.error(errorMessage);
    },
  });

  // Mutation para deletar relatório
  const deleteRelatorioMutation = useMutation({
    mutationFn: relatorioEstagioService.delete,
    onSuccess: async () => {
      toast.success('Relatório excluído com sucesso!');
      // Invalidar queries relacionadas
      await queryClient.invalidateQueries({ queryKey: ['relatorios-gerenciar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      // Forçar refetch dos relatórios do aluno
      await refetchRelatoriosAluno();
    },
    onError: (error: ApiError) => {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao excluir relatório';
      toast.error(errorMessage);
    },
  });

  // Funções auxiliares
  const getRelatorioPorTipo = (tipo: 'Inicial' | 'Parcial' | 'Final'): RelatorioEstagio | undefined => {
    if (!relatoriosDoEstagioSelecionado?.length) {
      return undefined;
    }
    const tipoBuscado = String(tipo).trim().toUpperCase();
    return relatoriosDoEstagioSelecionado.find((relatorio) => {
      if (!relatorio?.tipo) return false;
      return String(relatorio.tipo).trim().toUpperCase() === tipoBuscado;
    });
  };

  const getStatusInfo = (relatorio: RelatorioEstagio | undefined) => {
    if (!relatorio) {
      return { text: 'Não criado', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon };
    }
    if (relatorio.dataEntregue) {
      return { text: 'Entregue', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon };
    }
    const prazo = new Date(relatorio.prazoEntrega);
    const hoje = new Date();
    if (hoje > prazo) {
      return { text: 'Atrasado', color: 'bg-red-100 text-red-800', icon: ClockIcon };
    }
    return { text: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleCriarRelatorio = (estagioId: number, tipo: 'Inicial' | 'Parcial' | 'Final') => {
    setRelatorioEditando({ estagioId, tipo });
  };

  const handleEditarRelatorio = (relatorio: RelatorioEstagio) => {
    setRelatorioEditando({ estagioId: relatorio.estagioId, tipo: relatorio.tipo });
  };

  const handleDeletarRelatorio = (id: number, tipo: string) => {
    if (confirm(`Tem certeza que deseja excluir o ${tipo}? Esta ação não pode ser desfeita.`)) {
      deleteRelatorioMutation.mutate(id);
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <DocumentTextIcon className="h-8 w-8 text-primary-600" />
          Gerenciar Relatórios
        </h1>
        <p className="mt-1 text-gray-600">
          Selecione um estagiário e gerencie os relatórios dos seus estágios
        </p>
      </div>

      {/* Layout Principal: Sidebar + Conteúdo */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Sidebar de Estagiários */}
        <div className="w-80 flex-shrink-0 flex flex-col border border-gray-200 rounded-lg bg-white shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Estagiário</h2>
            <ServerSearchSelect<Aluno>
              value={alunoSelecionado?.id ?? null}
              onChange={(_selectedId, aluno) => {
                setAlunoSelecionado(aluno);
                setEstagioSelecionado(null);
                setRelatorioEditando(null);
              }}
              fetchPage={(params) => alunoService.list(params)}
              fetchById={(selectedId) => alunoService.getById(selectedId)}
              getOptionLabel={(aluno) => `${aluno.nome}${aluno.rm ? ` — RM ${aluno.rm}` : ''}`}
              queryKey="alunos-gerenciar-relatorios-select"
              placeholder="Buscar estagiário…"
            />
          </div>
          {alunoSelecionado && (
            <div className="p-4">
              <p className="font-semibold text-gray-900">{alunoSelecionado.nome}</p>
              {alunoSelecionado.curso && <p className="mt-1 text-sm text-gray-600">{alunoSelecionado.curso.nome}</p>}
            </div>
          )}
        </div>

        {/* Área Principal - Estágios e Relatórios */}
        <div className="flex-1 overflow-y-auto">
          {!alunoSelecionado ? (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Selecione um Estagiário
                </h3>
                <p className="text-gray-500">
                  Escolha um estagiário na lista ao lado para visualizar e gerenciar seus relatórios
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Informações do Aluno Selecionado */}
              <Card className="bg-gradient-to-r from-blue-50 to-primary-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <UserIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">{alunoSelecionado.nome}</h3>
                      <div className="text-sm text-gray-600 mt-1">
                        {alunoSelecionado.rm && `RM: ${alunoSelecionado.rm} • `}
                        {alunoSelecionado.curso?.nome}
                        {alunoSelecionado.serie && ` • ${alunoSelecionado.serie}`}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAlunoSelecionado(null);
                        setEstagioSelecionado(null);
                        setRelatorioEditando(null);
                      }}
                    >
                      Trocar Estagiário
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de Estágios */}
              <Card id="estagios-section">
                <CardHeader>
                  <CardTitle>Estágios do Estagiário</CardTitle>
                  <CardDescription>
                    Busque e selecione um estágio do estagiário.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ServerSearchSelect<Estagio>
                    value={estagioSelecionado?.id ?? null}
                    onChange={(_selectedId, estagio) => {
                      setEstagioSelecionado(estagio);
                      setRelatorioEditando(null);
                    }}
                    fetchPage={(params) => estagioService.list({ ...params, alunoId: alunoSelecionado.id })}
                    fetchById={(selectedId) => estagioService.getById(selectedId)}
                    getOptionLabel={(estagio) => `Estágio #${estagio.id} — ${estagio.empresa?.nomeFantasia ?? estagio.empresa?.nome ?? 'Empresa não informada'}`}
                    queryKey={`estagios-gerenciar-relatorios-${alunoSelecionado.id}`}
                    placeholder="Buscar estágio deste estagiário…"
                    emptyMessage="Nenhum estágio encontrado para este estagiário"
                  />
                  {!estagioSelecionado ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Selecione um estágio para visualizar os relatórios.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[estagioSelecionado].map((estagio) => {
                        const isOpen = estagioSelecionado?.id === estagio.id;

                        return (
                          <div
                            key={estagio.id}
                            className={`border rounded-lg p-4 transition-all ${isOpen
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                              }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold text-gray-900">Estágio #{estagio.id}</h4>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${estagio.status === 'Ativo' ? 'bg-green-100 text-green-800' :
                                    estagio.status === 'Concluido' ? 'bg-blue-100 text-blue-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                    {estagio.status}
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <BuildingOfficeIcon className="h-4 w-4" />
                                    <span>{estagio.empresa?.nome || 'Empresa não informada'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <CalendarIcon className="h-4 w-4" />
                                    <span>
                                      {formatDate(estagio.dataInicio)} - {formatDate(estagio.dataTermino)}
                                    </span>
                                  </div>
                                  {estagio.cargaHorariaSemanal && (
                                    <div>
                                      Carga Horária: {estagio.cargaHorariaSemanal} min/semana
                                    </div>
                                  )}
                                </div>
                              </div>
                              {!isOpen && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEstagioSelecionado(estagio);
                                    setRelatorioEditando(null);
                                  }}
                                >
                                  Ver Relatórios
                                </Button>
                              )}
                            </div>

                            {/* Relatórios do Estágio */}
                            {isOpen && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h5 className="font-medium text-gray-900 mb-3">
                                  Relatórios do Estágio
                                  {relatoriosDoEstagioSelecionado && (
                                    <span className="ml-2 text-sm text-gray-500">
                                      ({relatoriosDoEstagioSelecionado.length} encontrado(s))
                                    </span>
                                  )}
                                  {relatoriosDoEstagioSelecionado && relatoriosDoEstagioSelecionado.length > 0 && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Tipos encontrados: {relatoriosDoEstagioSelecionado.map(r => r.tipo).join(', ')}
                                    </div>
                                  )}
                                </h5>
                                {relatoriosAlunoLoading ? (
                                  <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                                    <p className="mt-2 text-sm text-gray-500">Carregando relatórios...</p>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {tiposRelatorio.map((tipo) => {
                                      // Só buscar relatórios se este estágio estiver selecionado e aberto
                                      const relatorio = isOpen && estagio.id === estagioSelecionado?.id
                                        ? getRelatorioPorTipo(tipo.value as 'Inicial' | 'Parcial' | 'Final')
                                        : undefined;

                                      const statusInfo = getStatusInfo(relatorio);
                                      const StatusIcon = statusInfo.icon;

                                      return (
                                        <Card key={tipo.value} className={`border-2 ${tipo.color === 'blue' ? 'border-blue-200' :
                                          tipo.color === 'yellow' ? 'border-yellow-200' :
                                            'border-green-200'
                                          }`}>
                                          <CardHeader className="pb-3">
                                            <CardTitle className="text-sm flex items-center justify-between">
                                              <span>{tipo.label}</span>
                                              <StatusIcon className={`h-5 w-5 ${statusInfo.color.includes('green') ? 'text-green-600' : statusInfo.color.includes('red') ? 'text-red-600' : statusInfo.color.includes('yellow') ? 'text-yellow-600' : 'text-gray-600'}`} />
                                            </CardTitle>
                                          </CardHeader>
                                          <CardContent className="space-y-3">
                                            <div>
                                              <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}>
                                                {statusInfo.text}
                                              </span>
                                            </div>
                                            {relatorio && (
                                              <>
                                                <div className="space-y-2 text-xs">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-500">Prazo de Entrega:</span>
                                                    <span className="font-medium text-gray-900">{formatDate(relatorio.prazoEntrega)}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-500">Data Entregue:</span>
                                                    <span className="font-medium text-gray-900">
                                                      {relatorio.dataEntregue ? formatDate(relatorio.dataEntregue) : 'Não entregue'}
                                                    </span>
                                                  </div>
                                                  {relatorio.observacao && (
                                                    <div className="pt-1 border-t border-gray-200">
                                                      <div className="text-gray-500 mb-1">Observações:</div>
                                                      <div className="text-gray-700 break-words" title={relatorio.observacao}>
                                                        {relatorio.observacao}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="flex gap-2 pt-2">
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex-1"
                                                    onClick={() => handleEditarRelatorio(relatorio)}
                                                  >
                                                    Editar
                                                  </Button>
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                                                    onClick={() => handleDeletarRelatorio(relatorio.id, tipo.label)}
                                                    disabled={deleteRelatorioMutation.isPending}
                                                  >
                                                    Excluir
                                                  </Button>
                                                </div>
                                              </>
                                            )}
                                            {!relatorio && (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                onClick={() => handleCriarRelatorio(estagio.id, tipo.value as 'Inicial' | 'Parcial' | 'Final')}
                                              >
                                                Criar Relatório
                                              </Button>
                                            )}
                                          </CardContent>
                                        </Card>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criar/Editar Relatório */}
      {relatorioEditando && estagioSelecionado && (
        <RelatorioFormModal
          estagioId={relatorioEditando.estagioId}
          tipo={relatorioEditando.tipo}
          relatorioExistente={getRelatorioPorTipo(relatorioEditando.tipo)}
          onClose={() => setRelatorioEditando(null)}
          onCreate={createRelatorioMutation.mutate}
          onUpdate={updateRelatorioMutation.mutate}
          isLoading={createRelatorioMutation.isPending || updateRelatorioMutation.isPending}
        />
      )}
    </div>
  );
};

// Componente Modal para Formulário de Relatório
interface RelatorioFormModalProps {
  estagioId: number;
  tipo: 'Inicial' | 'Parcial' | 'Final';
  relatorioExistente?: RelatorioEstagio;
  onClose: () => void;
  onCreate: (data: CreateRelatorioData) => void;
  onUpdate: (data: { id: number; data: Partial<RelatorioFormData> }) => void;
  isLoading: boolean;
}

const RelatorioFormModal: React.FC<RelatorioFormModalProps> = ({
  estagioId,
  tipo,
  relatorioExistente,
  onClose,
  onCreate,
  onUpdate,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RelatorioFormData>({
    resolver: zodResolver(relatorioSchema),
    defaultValues: {
      prazoEntrega: relatorioExistente
        ? new Date(relatorioExistente.prazoEntrega).toISOString().split('T')[0]
        : '',
      dataEntregue: relatorioExistente?.dataEntregue
        ? new Date(relatorioExistente.dataEntregue).toISOString().split('T')[0]
        : '',
      observacao: relatorioExistente?.observacao || '',
    },
  });

  const onSubmit = (data: RelatorioFormData) => {
    if (relatorioExistente) {
      onUpdate({
        id: relatorioExistente.id,
        data: {
          prazoEntrega: data.prazoEntrega,
          dataEntregue: data.dataEntregue || undefined,
          observacao: data.observacao || undefined,
        },
      });
    } else {
      onCreate({
        estagioId,
        tipo,
        prazoEntrega: data.prazoEntrega,
        dataEntregue: data.dataEntregue || undefined,
        observacao: data.observacao || undefined,
      });
    }
  };

  const tipoInfo = tiposRelatorio.find(t => t.value === tipo);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            {relatorioExistente ? 'Editar' : 'Criar'} {tipoInfo?.label}
          </CardTitle>
          <CardDescription>
            {relatorioExistente
              ? 'Edite as informações do relatório'
              : 'Preencha os dados para criar o relatório'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prazoEntrega">Prazo de Entrega *</Label>
              <Controller
                name="prazoEntrega"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="prazoEntrega"
                    type="date"
                    className={errors.prazoEntrega ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.prazoEntrega && (
                <p className="text-sm text-red-600">{errors.prazoEntrega.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dataEntregue">Data de Entrega</Label>
              <Controller
                name="dataEntregue"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="dataEntregue"
                    type="date"
                    className={errors.dataEntregue ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.dataEntregue && (
                <p className="text-sm text-red-600">{errors.dataEntregue.message}</p>
              )}
              <p className="text-xs text-gray-500">
                Deixe em branco se ainda não foi entregue
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacao">Observações</Label>
              <Controller
                name="observacao"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    id="observacao"
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.observacao ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Digite observações sobre o relatório..."
                  />
                )}
              />
              {errors.observacao && (
                <p className="text-sm text-red-600">{errors.observacao.message}</p>
              )}
            </div>

            <div className="flex gap-4 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-primary-600 hover:bg-primary-700"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{relatorioExistente ? 'Salvando...' : 'Criando...'}</span>
                  </div>
                ) : (
                  relatorioExistente ? 'Salvar Alterações' : 'Criar Relatório'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

